import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Eye, MousePointerClick, TrendingUp, Loader2, Tag, ArrowRight, Heart, MessageCircle, Send, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { getAnalytics, mediaUrl, getSocialAnalytics, refreshSocialMetrics } from "../lib/api";

const Stat = ({ icon: Icon, label, value, testid }) => (
  <div data-testid={testid} className="rounded-2xl border border-white/10 bg-[#11141E] p-6">
    <div className="flex items-center gap-2 text-slate-400 text-sm"><Icon className="h-4 w-4 text-[#FF6A5E]" /> {label}</div>
    <p className="mt-3 font-display font-extrabold text-4xl">{value}</p>
  </div>
);

export default function Analytics() {
  const [data, setData] = useState(null);
  const [social, setSocial] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => { getAnalytics().then(setData).catch(() => {}); getSocialAnalytics().then(setSocial).catch(() => {}); }, []);

  const refreshSocial = async () => {
    setRefreshing(true);
    try { await refreshSocialMetrics(); setSocial(await getSocialAnalytics()); toast.success("Metrics refreshed"); }
    catch { toast.error("Could not refresh"); }
    setRefreshing(false);
  };

  if (!data) {
    return <div className="flex items-center justify-center py-40 text-slate-400"><Loader2 className="h-6 w-6 animate-spin mr-2" /> Loading analytics…</div>;
  }

  return (
    <div className="max-w-6xl mx-auto px-5 sm:px-8 py-12">
      <span className="eyebrow">Affiliate performance</span>
      <h1 className="mt-2 font-display font-extrabold text-3xl sm:text-4xl tracking-tight">Analytics</h1>
      <p className="mt-2 text-slate-400 text-sm">Views and affiliate clicks tracked across your videos on the Faceless Ad Studio page.</p>

      <div className="mt-8 grid sm:grid-cols-3 gap-4">
        <Stat testid="stat-views" icon={Eye} label="Total Video Views" value={data.total_views} />
        <Stat testid="stat-clicks" icon={MousePointerClick} label="Total Affiliate Clicks" value={data.total_clicks} />
        <Stat testid="stat-ctr" icon={TrendingUp} label="Click-Through Rate" value={`${data.ctr}%`} />
      </div>

      <h2 className="mt-12 font-display font-bold text-lg">Per-video breakdown</h2>
      <div className="mt-4 overflow-hidden rounded-2xl border border-white/10">
        <div className="grid grid-cols-[1fr_auto_auto_auto] gap-4 px-5 py-3 bg-[#161A26] text-xs uppercase tracking-wide text-slate-500">
          <span>Video</span><span>Views</span><span>Clicks</span><span>CTR</span>
        </div>
        {data.videos.length === 0 && <div className="px-5 py-8 text-center text-slate-500 text-sm">No videos yet.</div>}
        {data.videos.map((v, i) => (
          <motion.div key={v.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: i * 0.03 }}
            data-testid={`analytics-row-${v.id}`}
            className="grid grid-cols-[1fr_auto_auto_auto] gap-4 items-center px-5 py-3 border-t border-white/5 hover:bg-white/[0.03]">
            <Link to={`/video/${v.id}`} className="flex items-center gap-3 min-w-0 group">
              <div className="h-12 w-8 shrink-0 overflow-hidden rounded bg-[#1E2333]">
                {v.thumb_path && <img src={mediaUrl(v.thumb_path)} alt="" className="h-full w-full object-cover" />}
              </div>
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-white group-hover:text-[#FF6A5E] transition-colors flex items-center gap-1.5">
                  {v.title} <ArrowRight className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                </p>
                {v.affiliate_enabled && <span className="inline-flex items-center gap-1 text-[11px] text-[#FF6A5E]"><Tag className="h-3 w-3" /> {v.product_name || "Affiliate"}</span>}
              </div>
            </Link>
            <span className="text-sm text-slate-300 tabular-nums">{v.views}</span>
            <span className="text-sm text-slate-300 tabular-nums">{v.clicks}</span>
            <span className="text-sm font-semibold text-white tabular-nums">{v.ctr}%</span>
          </motion.div>
        ))}
      </div>

      {social && (
        <div className="mt-14" data-testid="social-analytics">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <span className="eyebrow flex items-center gap-1.5"><Send className="h-3.5 w-3.5" /> Social reach</span>
              <h2 className="mt-1 font-display font-bold text-lg">Published post performance</h2>
            </div>
            <Button data-testid="refresh-social-metrics" onClick={refreshSocial} disabled={refreshing} variant="outline" className="rounded-full border-white/15 bg-transparent text-slate-200 hover:bg-white/5 text-sm">
              {refreshing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />} Refresh metrics
            </Button>
          </div>

          {!social.any_configured && (
            <div className="mt-4 rounded-2xl border border-white/10 bg-[#11141E] p-5 text-sm text-slate-400">
              Connect and publish to your social accounts to see views, likes and comments here. Platforms show data once their APIs are set up and posts go live. <Link to="/connections" className="text-[#FF6A5E] underline">Manage connections →</Link>
            </div>
          )}

          <div className="mt-4 grid sm:grid-cols-4 gap-4">
            <Stat testid="social-total-posts" icon={Send} label="Posts Published" value={social.total.posts} />
            <Stat testid="social-total-views" icon={Eye} label="Total Views" value={social.total.views} />
            <Stat testid="social-total-likes" icon={Heart} label="Total Likes" value={social.total.likes} />
            <Stat testid="social-total-comments" icon={MessageCircle} label="Total Comments" value={social.total.comments} />
          </div>

          {social.platforms.length > 0 && (
            <div className="mt-4 overflow-hidden rounded-2xl border border-white/10">
              <div className="grid grid-cols-[1fr_auto_auto_auto_auto] gap-4 px-5 py-3 bg-[#161A26] text-xs uppercase tracking-wide text-slate-500">
                <span>Platform</span><span>Posts</span><span>Views</span><span>Likes</span><span>Comments</span>
              </div>
              {social.platforms.map((p) => (
                <div key={p.platform} data-testid={`social-row-${p.platform}`} className="grid grid-cols-[1fr_auto_auto_auto_auto] gap-4 items-center px-5 py-3 border-t border-white/5">
                  <span className="text-sm font-medium text-white">{p.platform_name}{!p.configured && <span className="ml-2 text-[11px] text-amber-400">setup pending</span>}</span>
                  <span className="text-sm text-slate-300 tabular-nums">{p.posts}</span>
                  <span className="text-sm text-slate-300 tabular-nums">{p.views}</span>
                  <span className="text-sm text-slate-300 tabular-nums">{p.likes}</span>
                  <span className="text-sm text-slate-300 tabular-nums">{p.comments}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
